// The sessions, on the left: those that wait for him, those at work, those
// done, then the rest (format.ts says which); each with its title, its issue, its agent and model, a dot for
// its state and its age, and its runner where the workspace has more than
// one. A worker a lead delegated is reached through its lead, whose row
// says how many are at work. A session with a question put to him has an
// amber edge and says "Asks you"; one that asks someone else, "question". Moved from today's Code tab (code/SessionList.tsx
// there).
import { PlanLimits } from './PlanLimits'
import { useMemo, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { useBoardState } from '../store/context'
import { needsServer } from '../components/needsServer'
import type { Issue, Question, Runner, Session } from '../api/types'
import { Working } from '../components/Working'
import { useSessionPath } from '../projects/ProjectRoute'
import { putTo } from '../questions/asks'
import { cx } from '../transcript/bits'
import { Age, StateDot } from './bits'
import { activeRunners, agentLabel, countText, fullTitle, groupSessions, leadOf, listedSessions, recentSessions, stateText, titleOf, workersAtWork, type KnownTitle } from './format'
import { CloseIcon, PlusIcon } from './icons'

export interface SessionListProps {
  id: string
  /** The project's sessions, workers among them; null until the board is read. */
  sessions: readonly Session[] | null
  issues: ReadonlyMap<string, Issue>
  /** The open questions of each session that asked any. */
  asked: ReadonlyMap<string, readonly Question[]>
  /** The workspace's runners; null until read. */
  runners: readonly Runner[] | null
  /** What is known of the titles of sessions without an issue. */
  titles: ReadonlyMap<string, KnownTitle>
  selected: string | null
  /** The sessions he ended whose runner has not ended them yet: their rows say so. */
  ending?: ReadonlySet<string>
  onNew: () => void
  /** A row was chosen, or the drawer's own button pressed: where the list is a drawer, it closes. */
  onClose?: () => void
}

/** Up and Down move among the sessions, Home and End go to the first and the last. */
function stepThroughSessions(event: KeyboardEvent<HTMLElement>) {
  if (event.metaKey || event.ctrlKey || event.altKey) return
  const rows = [...event.currentTarget.querySelectorAll<HTMLElement>('a.code-session')]
  const at = rows.indexOf(document.activeElement as HTMLElement)
  if (at < 0) return
  let to: number
  if (event.key === 'ArrowDown') to = Math.min(rows.length - 1, at + 1)
  else if (event.key === 'ArrowUp') to = Math.max(0, at - 1)
  else if (event.key === 'Home') to = 0
  else if (event.key === 'End') to = rows.length - 1
  else return
  event.preventDefault()
  rows[to]?.focus()
}

function SessionRow({
  session,
  issue,
  known,
  asking,
  asksMe,
  current,
  ending,
  crew,
  runners,
  showRunner,
  onPick,
}: {
  session: Session
  issue: Issue | undefined
  known: KnownTitle | undefined
  asking: boolean
  /** One of its open questions is put to him. */
  asksMe: boolean
  /** The open session, or the lead of the open one that this list leaves out. */
  current: boolean | 'lead'
  ending: boolean
  /** How many of its workers are at work. */
  crew: number
  runners: readonly Runner[] | null
  showRunner: boolean
  onPick?: () => void
}) {
  const pathOf = useSessionPath()
  const lead = leadOf(session)
  const title = titleOf(session, issue, known) || (lead ? 'Untitled subagent' : 'Untitled session')
  const agent = agentLabel(session, runners)
  const runner = showRunner ? runners?.find((r) => r.id === session.runner_id) : undefined
  const crewText = crew > 0 ? countText(crew, 'subagent') : ''
  return (
    <li>
      <Link
        to={pathOf(session.id)}
        className={cx('code-session', current && 'is-current', `is-${session.state}`, asksMe && !ending && 'is-asking')}
        aria-current={current === 'lead' ? 'true' : current ? 'page' : undefined}
        title={(lead ? '' : fullTitle(issue, session.title, known)) || title}
        onClick={onPick}
      >
        <StateDot session={session} asking={asking} silent />
        {/* The spaces are for a screen reader: the boxes they stand between take none. */}
        <span className="code-session-text">
          <span className="code-session-title">{title}</span>
          {/* What the dot says, after the title: a row is known by its name first. */}
          <span className="visually-hidden">{`, ${ending ? 'Ending' : stateText(session, asking)}${crewText && ` · ${crewText}`}.`}</span>{' '}
          <span className="code-session-meta">
            {issue && (
              <span className="code-session-issue" data-state={issue.state}>
                {issue.key}
              </span>
            )}{' '}
            {/* Its agent's colour: a dot, and the agent's name, then the model chosen. */}
            <span className={cx('code-session-engine', agent.id && `is-${agent.id}`)} title={session.agent.vendor}>
              <span className="code-engine-dot" aria-hidden="true" />
              <span className="code-engine-name">{agent.name}</span>
              {agent.model ? ` · ${agent.model}` : ''}
            </span>{' '}
            {asking && !ending && (asksMe ? <span className="code-session-asks">Asks you</span> : <span className="code-session-waits">question</span>)}{' '}
            {ending && <span className="code-session-ending">Ending…</span>}
            {/* Said once, in the words above. */}
            {crewText && (
              <span className="code-session-crew" aria-hidden="true">
                {crewText}
              </span>
            )}
          </span>
        </span>{' '}
        <span className="code-session-age">
          <Age iso={session.last_active_at} label="Last active" />
          {/* Where it runs, under its age, where the workspace has more than one runner. */}
          {runner && (
            <>
              {' '}
              <span className="code-session-runner" title={runner.machine ?? undefined}>
                {runner.name}
              </span>
            </>
          )}
        </span>
      </Link>
    </li>
  )
}

const NO_ENDING: ReadonlySet<string> = new Set()

export function SessionList({ id, sessions, issues, asked, runners, titles, selected, ending = NO_ENDING, onNew, onClose }: SessionListProps) {
  const { offline, me, proposals, landings, held, dismissed } = useBoardState()
  const member = me?.member?.id
  const listed = useMemo(() => listedSessions(recentSessions(sessions ?? [], Date.now())), [sessions])
  const groups = useMemo(() => groupSessions(listed, { asked, member, proposals, landings, held, dismissed, sessions: sessions ?? [] }),
    [listed, asked, member, proposals, landings, held, dismissed, sessions])
  // A worker the list leaves out is open: its lead's row stands for it.
  const open = sessions?.find((session) => session.id === selected)
  const lead = open && !listed.includes(open) ? leadOf(open) : null
  const showRunner = activeRunners(runners).length >= 2
  return (
    <aside className="code-list" id={id} aria-label="Sessions">
      <div className="code-list-head">
        <button type="button" className="button code-new" disabled={offline} {...needsServer(offline)} onClick={onNew}>
          <PlusIcon size={13} />
          New session
        </button>
        {onClose && (
          <button type="button" className="button button-quiet code-icon-button code-list-close" aria-label="Close" onClick={onClose}>
            <CloseIcon size={14} />
          </button>
        )}
      </div>
      <nav className="code-list-scroll" onKeyDown={stepThroughSessions}>
        {sessions === null && (
          <p className="code-list-note" aria-busy="true">
            <Working />
          </p>
        )}
        {sessions !== null && groups.length === 0 && <p className="code-list-note muted">No session in the last seven days</p>}
        {groups.map(({ group, title, sessions: list }) => (
          <section key={group} className={`code-group code-group-${group}`} aria-label={title}>
            <h2 className="code-group-title">
              {title}
              <span className="code-group-count">{list.length}</span>
            </h2>
            <ul className="code-session-list">
              {list.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  issue={session.issue_id ? issues.get(session.issue_id) : undefined}
                  known={titles.get(session.id)}
                  asking={asked.has(session.id)}
                  asksMe={asked.get(session.id)?.some((question) => putTo(question, member)) ?? false}
                  current={session.id === selected || (session.id === lead && 'lead')}
                  ending={ending.has(session.id)}
                  crew={sessions ? workersAtWork(sessions, session.id) : 0}
                  runners={runners}
                  showRunner={showRunner}
                  onPick={onClose}
                />
              ))}
            </ul>
          </section>
        ))}
      </nav>
      <PlanLimits runners={runners} />
    </aside>
  )
}
